import { toast as sonnerToast } from 'sonner';

import type { BugReportPrefill } from '../contexts/BugReportContext';

type BugReporter = (prefill?: BugReportPrefill) => void;

let bugReporter: BugReporter | null = null;

/**
 * Registra (ou desregistra com `null`) a função global usada por `toastError`
 * para abrir o modal de "Reportar bug" pré-populado. Chamada pelo
 * `BugReportProvider` em um `useEffect` para evitar import circular entre
 * `lib/` e `contexts/`.
 */
export function registerBugReporter(fn: BugReporter | null): void {
  bugReporter = fn;
}

export interface ToastErrorOptions {
  /** requestId vindo do backend (header ou body) para correlação. */
  errorRequestId?: string;
  /** Mensagem técnica de erro (ex.: `err.message`). */
  errorMessage?: string;
  /**
   * Quando `true`, esconde o botão "Reportar bug" — usar em fluxos onde o
   * próprio bug report falhou (evita loop de "reportar bug do bug") ou em
   * páginas pré-auth (sem usuário associável).
   */
  noReport?: boolean;
}

/**
 * Helper central para exibir toasts de erro com ação opcional de reportar bug.
 *
 * Quando o `BugReportProvider` está montado (registrado via
 * `registerBugReporter`), o toast inclui um botão "Reportar bug" que abre o
 * modal pré-populado com a mensagem, URL atual, e requestId do backend.
 */
export function toastError(message: string, opts?: ToastErrorOptions): void {
  const shouldShowAction = !opts?.noReport && bugReporter !== null;
  sonnerToast.error(message, {
    duration: 8000,
    action: shouldShowAction
      ? {
          label: 'Reportar bug',
          onClick: () => {
            const reporter = bugReporter;
            if (!reporter) return;
            reporter({
              title: `Erro: ${message.slice(0, 100)}`,
              description: `Encontrei este erro:\n\n${opts?.errorMessage || message}\n\nUrl: ${window.location.pathname}`,
              errorRequestId: opts?.errorRequestId,
              errorMessage: opts?.errorMessage || message,
            });
          },
        }
      : undefined,
  });
}

/**
 * Tenta extrair um `requestId` (correlação com logs do backend) de um erro
 * desconhecido. Convenção: o helper procura `err.requestId` e, em fallback,
 * `err.body.requestId` — campos típicos quando o caller já fez `await
 * res.json()` em uma resposta não-OK.
 */
export function extractRequestId(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const direct = (error as { requestId?: unknown }).requestId;
  if (typeof direct === 'string') return direct;
  const body = (error as { body?: unknown }).body;
  if (body && typeof body === 'object') {
    const fromBody = (body as { requestId?: unknown }).requestId;
    if (typeof fromBody === 'string') return fromBody;
  }
  return undefined;
}
